import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingNavbar.css';

const LandingNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Prevent body scroll when sidebar is open
    if (isMenuOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleProductMenu = () => {
    setIsProductMenuOpen(!isProductMenuOpen);
  };

  const closeProductMenu = () => {
    setIsProductMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleLoginClick = () => {
    navigate('/client-login');
    setIsMenuOpen(false);
  };

  const closeSidebar = () => {
    setIsMenuOpen(false);
    setIsProductMenuOpen(false);
  };

  return (
    <>
      <nav className={`landing-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="navbar-container">
          <div className="navbar-content">
            <div className="navbar-brand">
              <div className="brand-logo">
                <img src="/images/logo.png" alt="Company Logo" className="logo-image" />
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="navbar-menu">
              <ul className="navbar-nav">
                <li className="nav-item">
                  <button 
                    className="nav-link" 
                    onClick={() => scrollToSection('hero')}
                  >
                    Home
                  </button>
                </li>
                <li className="nav-item dropdown-container">
                  <button 
                    className="nav-link dropdown-trigger" 
                    onClick={toggleProductMenu}
                    onMouseEnter={() => setIsProductMenuOpen(true)}
                  >
                    Products
                    <span className={`dropdown-arrow ${isProductMenuOpen ? 'open' : ''}`}>▼</span>
                  </button>
                  {isProductMenuOpen && (
                    <div 
                      className="product-dropdown"
                      onMouseLeave={closeProductMenu}
                    >
                      <div className="product-dropdown-content">
                        <div className="product-image">
                          <img src="/images/product.png" alt="Water Level Controller" />
                        </div>
                        <div className="product-info">
                          <h3>Wireless Water Level Indicator & Alarm System</h3>
                          <p>
                            The Wireless Water Level Indicator & Alarm System is designed to provide a simple, reliable, and maintenance-free solution for monitoring water levels in overhead tanks, underground reservoirs, and sumps. Operating on a secure 433–434 MHz wireless frequency, the system ensures smooth communication between the transmitter and the indicator, with a range of up to 500 meters in line of sight conditions.
                          </p>
                          <div className="product-features">
                            <div className="feature-point">✓ Wireless monitoring up to 500m range</div>
                            <div className="feature-point">✓ Water levels in 20%, 40%, 60%, 80%, 100% steps</div>
                            <div className="feature-point">✓ 10-year battery life transmitter</div>
                            <div className="feature-point">✓ IP-67 water & dust resistant</div>
                          </div>
                          <button className="btn btn-primary" onClick={() => scrollToSection('contact')}>
                            Learn More
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
                <li className="nav-item">
                  <button 
                    className="nav-link" 
                    onClick={() => scrollToSection('features')}
                  >
                    Features
                  </button>
                </li>
                <li className="nav-item">
                  <button 
                    className="nav-link" 
                    onClick={() => scrollToSection('services')}
                  >
                    Services
                  </button>
                </li>
                <li className="nav-item">
                  <button 
                    className="nav-link" 
                    onClick={() => scrollToSection('about')}
                  >
                    About
                  </button>
                </li>
                <li className="nav-item">
                  <button 
                    className="nav-link" 
                    onClick={() => scrollToSection('contact')}
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Desktop Actions */}
            <div className="navbar-actions">
              <button className="demo-btn" onClick={() => scrollToSection('contact')}>
                Request Demo
              </button>
              <button 
                className="login-btn-desktop" 
                onClick={handleLoginClick}
              >
                Login
              </button>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className={`mobile-menu-toggle ${isMenuOpen ? 'active' : ''}`}
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      <div 
        className={`mobile-sidebar-overlay ${isMenuOpen ? 'active' : ''}`}
        onClick={closeSidebar}
      />

      {/* Mobile Sidebar */}
      <div className={`mobile-sidebar ${isMenuOpen ? 'active' : ''}`}>
        {/* Close Button */}
        <button className="sidebar-close" onClick={closeSidebar}>
          ×
        </button>

        {/* Sidebar Header */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <img src="/images/logo.png" alt="Company Logo" className="sidebar-logo" />
          </div>
        </div>

        {/* Sidebar Navigation */}
        <nav className="sidebar-nav">
          <ul className="sidebar-nav-list">
            <li className="sidebar-nav-item">
              <button 
                className="sidebar-nav-link" 
                onClick={() => scrollToSection('hero')}
              >
                🏠 Home
              </button>
            </li>
            <li className="sidebar-nav-item">
              <button 
                className="sidebar-nav-link" 
                onClick={() => scrollToSection('products')}
              >
                📦 Products
              </button>
            </li>
            <li className="sidebar-nav-item">
              <button 
                className="sidebar-nav-link" 
                onClick={() => scrollToSection('features')}
              >
                ⚡ Features
              </button>
            </li>
            <li className="sidebar-nav-item">
              <button 
                className="sidebar-nav-link" 
                onClick={() => scrollToSection('services')}
              >
                🛠️ Services
              </button>
            </li>
            <li className="sidebar-nav-item">
              <button 
                className="sidebar-nav-link" 
                onClick={() => scrollToSection('about')}
              >
                ℹ️ About
              </button>
            </li>
            <li className="sidebar-nav-item">
              <button 
                className="sidebar-nav-link" 
                onClick={() => scrollToSection('contact')}
              >
                📧 Contact
              </button>
            </li>
          </ul>
        </nav>

        {/* Sidebar Actions */}
        <div className="sidebar-actions">
          <button 
            className="sidebar-btn sidebar-demo-btn" 
            onClick={() => scrollToSection('contact')}
          >
            Request Demo
          </button>
          <button 
            className="sidebar-btn sidebar-login-btn" 
            onClick={handleLoginClick}
          >
            Login
          </button>
        </div>
      </div>
    </>
  );
};

export default LandingNavbar;
